import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { forkJoin, Subject } from "rxjs";
import { CommonSelectionInterface } from "../../share/interface/common.interface";
import { AlertService } from "../../share/service/alert.service";
import { ActivatedRoute, Router } from "@angular/router";
import { debounceTime, takeUntil, tap } from "rxjs/operators";
import { Sort } from "@angular/material/sort";
import { PageEvent } from "@angular/material/paginator";
import { PARTNER_STATUS_OPTIONS, PartnerStatus } from "../../share/constants/partner-platform/partner-status";
import { O2OPartnerService } from "../services/o2o-partner.service";
import { O2OPartnerListingInterface, O2OPartnerSummaryInterface } from "../interfaces/o2o-partner.interface";
import { triggerFileDownload } from "../../share/helpers/downloadFile.helper";

@Component({
  selector: "app-partner",
  templateUrl: "./partner.component.html",
  styleUrls: ["./partner.component.scss"]
})
export class PartnerComponent implements OnInit, AfterViewInit, OnDestroy {
  summaryCount: O2OPartnerSummaryInterface;

  statusList: CommonSelectionInterface[] = [];
  partnerStatusList: CommonSelectionInterface[] = [];

  filterStatusList: CommonSelectionInterface[] = [];
  filterPartnerStatusList: CommonSelectionInterface[] = [];

  selectedStatusList: string[] = [];
  selectedPartnerStatusList: string[] = [];

  searchText: string;
  searchChange: Subject<void> = new Subject<void>();

  sortIndex: number = 0;
  sortDirection: "asc" | "desc" = "desc";

  dataSource: O2OPartnerListingInterface[] = [];

  isLoading = false;
  displayedColumns: string[] = [
    "date",
    "partnerName",
    "registrationNo",
    "category",
    "protections",
    "branches",
    "status",
    "action"
  ];
  status: string[] = [];
  limit: number = 50;
  offset: number = 1;
  length = 0;
  selected: any;

  @ViewChild("paginator") paginator;
  protected readonly PartnerStatus = PartnerStatus;
  private unsubscribe$ = new Subject<void>();

  constructor(
    private alertService: AlertService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private o2oPartnerService: O2OPartnerService
  ) {}

  ngOnInit(): void {
    this.getListSelection();

    this.searchChange
      .pipe(debounceTime(500), takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.filter();
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngAfterViewInit(): void {
    this.resumeFilterQueryParams();
    this.fetchInitialData();
  }

  //#region policy status filter
  filterPolicyStatusSelection(searchValue: string = null) {
    if (searchValue) {
      let txt = searchValue.toLowerCase().trim();
      this.filterPartnerStatusList = this.partnerStatusList.filter((x) =>
        x.name.toLowerCase().includes(txt)
      );
    } else {
      this.filterPartnerStatusList = Object.assign([], this.partnerStatusList);
    }
  }

  isSelectedAllPolicyStatus() {
    return (
      this.selectedPartnerStatusList.length == this.partnerStatusList.length
    );
  }

  isIndeterminatePolicyStatus() {
    if (this.isSelectedAllPolicyStatus()) return false;
    return this.selectedPartnerStatusList.length > 0;
  }

  toggleSelectAllPolicyStatus(val: boolean, hasFilter: boolean = true) {
    this.selectedPartnerStatusList = [];
    if (val) {
      this.selectedPartnerStatusList = this.partnerStatusList.map((y) => y.id);
      if (hasFilter) {
        this.filter();
      }
    } else {
      this.dataSource = [];
      this.length = 0;
    }
  }

  getPolicyStatusDisplay() {
    if (this.selectedPartnerStatusList.length == 1)
      return this.partnerStatusList.find(
        (x) => x.id == this.selectedPartnerStatusList[0]
      ).name;
    else if (this.isSelectedAllPolicyStatus()) return "All partner status";
    else
      return `${this.selectedPartnerStatusList.length} partner status selected`;
  }

  resetPolicyStatus() {
    event.stopImmediatePropagation();
    this.toggleSelectAllPolicyStatus(true);
  }

  sortChange(sortState: Sort) {
    this.sortDirection = null;
    this.sortIndex = -1;
    if (sortState.direction) {
      this.sortDirection = sortState.direction;
      this.sortIndex = this.displayedColumns.findIndex(
        (x) => x == sortState.active
      );
    }
    if (this.dataSource.length == 0) return;

    this.getData();
  }

  resetTextFilter() {
    event.stopImmediatePropagation();
    this.searchText = null;
    this.filter();
  }

  pageChange(e: PageEvent) {
    this.offset = e.pageIndex + 1;
    this.limit = e.pageSize;
    this.getData();
  }

  filter() {
    if (this.selectedPartnerStatusList.length == 0) {
      this.selectedPartnerStatusList = this.partnerStatusList.map((y) => y.id);
    }
    this.offset = 1;
    this.getData();
  }

  exportPolicies(): void {
    this.o2oPartnerService
      .getPolicyListingExcel(
        this.limit,
        this.offset,
        this.searchText,
        this.sortIndex,
        this.sortDirection,
        null,
        null,
        this.selectedPartnerStatusList
      )
      .subscribe({
        next: (res) => {
          const date = new Date().toISOString().split("T")[0]; // yyyy-MM-dd
          triggerFileDownload(res, `Partners_${date}.xlsx`);
        },
        error: (err) => this.alertService.openSnackBar(err.error)
      });
  }

  addPartner(): void {
    this.router.navigate(["save"], { relativeTo: this.route });
  }

  viewDetails(element: O2OPartnerListingInterface): void {
    this.router.navigate([element.id], { relativeTo: this.route });
  }

  private getPartnerSummary$() {
    return this.o2oPartnerService.getPartnerSummary().pipe(
      tap({
        next: (res) => (this.summaryCount = res)
      })
    );
  }

  private fetchInitialData(): void {
    forkJoin([this.getPartnerSummary$(), this.getListing$()]).subscribe({
      error: (err) => this.alertService.openSnackBar(err.error)
    });
  }

  private getData(): void {
    this.isLoading = true;
    this.dataSource = [];

    this.getListing$()
      .subscribe({
        error: (err) => this.alertService.openSnackBar(err.error)
      })
      .add(() => (this.isLoading = false));
  }

  private getListing$() {
    return this.o2oPartnerService
      .getListing(
        this.limit,
        this.offset,
        this.searchText,
        this.sortIndex,
        this.sortDirection,
        null,
        null,
        this.selectedPartnerStatusList
      )
      .pipe(
        tap({
          next: (res) => {
            this.dataSource = res.data;
            this.length = res.count;
            this.applyFilterToUrl();
          }
        })
      );
  }

  private getListSelection(): void {
    this.partnerStatusList = PARTNER_STATUS_OPTIONS;

    this.filterPolicyStatusSelection();
    this.toggleSelectAllPolicyStatus(true, false);
  }

  private resumeFilterQueryParams(): void {
    let queryParams = this.route.snapshot.queryParams;

    // Setting for Policy status filter
    if (queryParams?.partnerStatus) {
      let partnerStatusQuery = queryParams.partnerStatus;
      if (
        typeof partnerStatusQuery === "string" ||
        partnerStatusQuery instanceof String
      ) {
        partnerStatusQuery = [queryParams.partnerStatus];
      }
      this.selectedPartnerStatusList = queryParams?.partnerStatus?.length
        ? partnerStatusQuery
        : this.partnerStatusList.map((x) => x.id);
    }

    //Setting for page offset filter
    if (queryParams?.offset) {
      this.offset = queryParams.offset || 1;
      this.paginator.pageIndex = this.offset - 1;
    }

    if (queryParams?.sortIndex !== 0) {
      this.sortIndex = queryParams.sortIndex;
    }

    if (queryParams?.sortDirection) {
      this.sortDirection = queryParams.sortDirection;
    }

    //Setting for search filter
    this.searchText = queryParams?.filter;

    this.cdr.detectChanges();
  }

  private applyFilterToUrl() {
    const partnerStatusAreFiltered =
      this.selectedPartnerStatusList.length > 0 &&
      this.partnerStatusList.length !== this.selectedPartnerStatusList.length;

    if (
      this.searchText ||
      partnerStatusAreFiltered ||
      this.sortIndex !== -1 ||
      this.sortDirection
    ) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          filter: this.searchText?.length > 0 ? this.searchText : null,
          partnerStatus: partnerStatusAreFiltered
            ? this.selectedPartnerStatusList
            : null,
          sortIndex: this.sortIndex !== 0 ? this.sortIndex : null,
          sortDirection:
            this.sortDirection !== "asc" ? this.sortDirection : null,
          offset: this.offset !== 1 ? this.offset : null
        },
        queryParamsHandling: "merge",
        replaceUrl: true
      });
    } else {
      this.router.navigate([]);
    }
  }
}
